import React from 'react';
import { fireEvent, render, screen } from '@testing-library/react';

import ConfigProvider from 'components/ConfigProvider';

import PermissionsStep from './PermissionsStep';
import { PERMISSIONS_STEP } from './copy';

const OPTIONS = [
  { id: 'fleet-administrator', label: 'Fleet Administrator', roleId: 'r1' },
  { id: 'report-user', label: 'Report User', roleId: 'r2' },
  {
    id: 'management-role',
    label: 'Management Role',
    choices: [
      { label: 'Member', roleId: 'r3' },
      { label: 'Manager', roleId: 'r4' },
    ],
  },
];

const DRAFT = {
  hiddenExternal: false,
  hiddenInternal: false,
  managementRoleId: '',
  permissionIds: [],
};

const onChange = jest.fn();

const renderStep = (props = {}) =>
  render(
    <ConfigProvider>
      <PermissionsStep
        accountCount={1}
        draft={DRAFT}
        permissionOptions={OPTIONS}
        onChange={onChange}
        {...props}
      />
    </ConfigProvider>,
  );

beforeEach(() => onChange.mockClear());

describe('management/Users PermissionsStep', () => {
  it('offers the three permission rows the design shows', () => {
    renderStep();

    expect(screen.getByLabelText('Fleet Administrator')).toBeInTheDocument();
    expect(screen.getByLabelText('Report User')).toBeInTheDocument();
    expect(screen.getByLabelText('Management Role')).toBeInTheDocument();
  });

  it('ticks a single role by its server-supplied id', () => {
    renderStep();

    fireEvent.click(screen.getByLabelText('Fleet Administrator'));

    expect(onChange).toHaveBeenCalledWith('permissionIds', ['r1']);
  });

  it('unticks a role that was on', () => {
    renderStep({ draft: { ...DRAFT, permissionIds: ['r1', 'r2'] } });

    fireEvent.click(screen.getByLabelText('Fleet Administrator'));

    expect(onChange).toHaveBeenCalledWith('permissionIds', ['r2']);
  });

  describe('the management role group', () => {
    // Member and Manager are mutually exclusive, hence radios under a checkbox
    // rather than a third checkbox.
    it('hides its choices until the group is on', () => {
      renderStep();

      expect(screen.queryByLabelText('Member')).not.toBeInTheDocument();
    });

    it('defaults to the first choice when ticked', () => {
      renderStep();

      fireEvent.click(screen.getByLabelText('Management Role'));

      expect(onChange).toHaveBeenCalledWith('managementRoleId', 'r3');
    });

    it('shows its choices once on', () => {
      renderStep({ draft: { ...DRAFT, managementRoleId: 'r3' } });

      expect(screen.getByLabelText('Member')).toBeInTheDocument();
      expect(screen.getByLabelText('Manager')).toBeInTheDocument();
    });

    it('swaps the chosen role', () => {
      renderStep({ draft: { ...DRAFT, managementRoleId: 'r3' } });

      fireEvent.click(screen.getByLabelText('Manager'));

      expect(onChange).toHaveBeenCalledWith('managementRoleId', 'r4');
    });

    // Otherwise a retick would silently reuse whichever role was chosen before.
    it('clears the chosen role when unticked', () => {
      renderStep({ draft: { ...DRAFT, managementRoleId: 'r4' } });

      fireEvent.click(screen.getByLabelText('Management Role'));

      expect(onChange).toHaveBeenCalledWith('managementRoleId', '');
    });
  });

  // CREATE USER is disabled until one is chosen (stepValidity[3]), and the step
  // opens with none, so the rule has to be on screen or the button looks broken.
  describe('the "at least one permission" rule', () => {
    it('is stated while nothing is selected', () => {
      renderStep();

      expect(
        screen.getByText(PERMISSIONS_STEP.permissionsRequired),
      ).toBeInTheDocument();
    });

    it('goes away once a single role is on', () => {
      renderStep({ draft: { ...DRAFT, permissionIds: ['r1'] } });

      expect(
        screen.queryByText(PERMISSIONS_STEP.permissionsRequired),
      ).not.toBeInTheDocument();
    });

    // Ticking only Management Role leaves permissionIds empty but is a valid
    // selection, so the message must read managementRoleId too.
    it('goes away when only the management role is on', () => {
      renderStep({ draft: { ...DRAFT, managementRoleId: 'r3' } });

      expect(
        screen.queryByText(PERMISSIONS_STEP.permissionsRequired),
      ).not.toBeInTheDocument();
    });
  });

  describe('the multi-account notice', () => {
    it('appears for more than one account', () => {
      renderStep({ accountCount: 2 });

      expect(
        screen.getByText(PERMISSIONS_STEP.multiAccountNotice),
      ).toBeInTheDocument();
    });

    // With one account there is nothing to distinguish, so it would be noise.
    it('is absent for a single account', () => {
      renderStep({ accountCount: 1 });

      expect(
        screen.queryByText(PERMISSIONS_STEP.multiAccountNotice),
      ).not.toBeInTheDocument();
    });
  });

  describe('discoverability', () => {
    it('offers both flags, worded as the legacy page words them', () => {
      renderStep();

      expect(
        screen.getByLabelText(PERMISSIONS_STEP.hiddenExternal),
      ).toBeInTheDocument();
      expect(
        screen.getByLabelText(PERMISSIONS_STEP.hiddenInternal),
      ).toBeInTheDocument();
    });

    it('reports the external flag', () => {
      renderStep();

      fireEvent.click(screen.getByLabelText(PERMISSIONS_STEP.hiddenExternal));

      expect(onChange).toHaveBeenCalledWith('hiddenExternal', true);
    });

    it('reports the internal flag turning off again', () => {
      renderStep({ draft: { ...DRAFT, hiddenInternal: true } });

      fireEvent.click(screen.getByLabelText(PERMISSIONS_STEP.hiddenInternal));

      expect(onChange).toHaveBeenCalledWith('hiddenInternal', false);
    });
  });

  // An unseeded environment offers fewer rows rather than failing.
  it('renders whatever rows the server offers', () => {
    renderStep({ permissionOptions: [OPTIONS[1]] });

    expect(screen.getByLabelText('Report User')).toBeInTheDocument();
    expect(
      screen.queryByLabelText('Fleet Administrator'),
    ).not.toBeInTheDocument();
    expect(screen.queryByLabelText('Management Role')).not.toBeInTheDocument();
  });
});
